import { useEffect, useState, ReactNode } from 'react'

import { CityPhoto } from './style'

type GoogleMapsImageProps = {
  name: string
  latitude?: number | string
  longitude?: number | string
  fallbackImage?: string
  children: ReactNode
}

const GoogleMapsImage = ({
  name,
  latitude,
  longitude,
  fallbackImage,
  children
}: GoogleMapsImageProps) => {
  const [cityImage, setCityImage] = useState<string | undefined>(fallbackImage)

  useEffect(() => {
    setCityImage(fallbackImage)

    if (fallbackImage) return

    let cancelled = false
    let attempts = 0
    let timer: ReturnType<typeof setTimeout> | undefined

    const getPlacePhoto = () => {
      attempts += 1

      if (!window.google?.maps?.places || !window.google?.maps?.Geocoder) {
        if (attempts < 20) timer = setTimeout(getPlacePhoto, 250)
        return
      }

      const parsedLatitude = Number(latitude)
      const parsedLongitude = Number(longitude)
      const hasCoordinates =
        Number.isFinite(parsedLatitude) && Number.isFinite(parsedLongitude)
      const location = hasCoordinates
        ? new window.google.maps.LatLng(parsedLatitude, parsedLongitude)
        : undefined

      const findPhoto = (country?: string) => {
        const service = new window.google.maps.places.PlacesService(
          document.createElement('div')
        )
        const query = [name.split(',')[0].trim(), country]
          .filter(Boolean)
          .join(', ')
        const request: any = {
          query,
          fields: ['name', 'photos', 'geometry']
        }

        if (location) request.locationBias = location

        service.findPlaceFromQuery(request, (results, status) => {
          if (cancelled) return

          if (
            status === window.google.maps.places.PlacesServiceStatus.OK &&
            results?.[0]?.photos?.[0]
          ) {
            const photoUrl = results[0].photos[0].getUrl({
              maxWidth: 1800,
              maxHeight: 900
            })
            setCityImage(photoUrl)
          }
        })
      }

      if (!location) {
        findPhoto()
        return
      }

      const geocoder = new window.google.maps.Geocoder()
      geocoder.geocode({ location }, (results, status) => {
        if (cancelled) return

        if (status !== 'OK' || !results?.length) {
          findPhoto()
          return
        }

        const country = results
          .flatMap((result) => result.address_components || [])
          .find((component) => component.types.includes('country'))?.long_name

        findPhoto(country)
      })
    }

    getPlacePhoto()

    return () => {
      cancelled = true
      if (timer) clearTimeout(timer)
    }
  }, [fallbackImage, latitude, longitude, name])

  return (
    <CityPhoto id="city-photo" src={cityImage}>
      {children}
    </CityPhoto>
  )
}

export default GoogleMapsImage
